import * as React from 'react';
import { Link } from 'react-router-dom';
import styles from './AlertHeader.module.css';
import { Panel, Avatar, useAlertBackground, BatteryIndicator } from '@ixicare/ui';
import { t } from '~/i18n';
import Alert from '~store/Alert/Alert';
import { useQuery } from '@tanstack/react-query';
import {
  Device,
  fetchLivingUnit,
  Profile,
  deviceTypesWithAccurateBatteryPercentage,
  deviceTypesWithOwner,
} from '@ixicare/resources';
import { Alert as AlertBox } from '@ixicare/ui';

interface AlertHeaderProps {
  alert: Alert;
  parentAlert?: Alert;
}

const AlertHeader: React.FC<AlertHeaderProps> = ({ alert, parentAlert }) => {
  const alarmGradientStyle = useAlertBackground(alert.priority);
  const parentGradientStyle = useAlertBackground(parentAlert?.priority ?? alert.priority);
  const { data: resident } = useQuery({
    queryKey: ['profile', alert.resident?.id],
    queryFn: () => Profile.getProfile(alert.resident?.id ?? -1),
    enabled: !!alert.resident,
  });
  const { data: livingUnit } = useQuery({
    queryKey: ['livingUnit', alert.resident?.id],
    queryFn: () => fetchLivingUnit(resident?.livingUnitId ?? ''),
    enabled: !!resident?.livingUnitId,
  });
  const { data: device } = useQuery({
    queryKey: ['device', alert.device?.id],
    queryFn: () => Device.getDevice(alert.device?.id ?? -1),
    enabled: !!alert.device,
  });

  const { data: parentResident } = useQuery({
    queryKey: ['profile', parentAlert?.resident?.id],
    queryFn: () => Profile.getProfile(parentAlert?.resident?.id ?? -1),
    enabled: !!parentAlert?.resident,
  });
  const { data: parentLivingUnit } = useQuery({
    queryKey: ['livingUnit', parentAlert?.resident?.id],
    queryFn: () => fetchLivingUnit(parentResident?.livingUnitId ?? ''),
    enabled: !!parentResident?.livingUnitId,
  });

  const deviceImage = device?.deviceType ? `/assets/${device.deviceType.toLowerCase()}@2x.png` : undefined;
  const showDeviceImage = !alert.resident;

  if (parentAlert) {
    const parentLocation = parentLivingUnit
      ? `${parentLivingUnit.name} - ${parentResident?.site?.name}`
      : (parentResident?.site?.name ?? parentAlert.locationDescription ?? '');

    const childInitials = alert.resident
      ? `${alert.resident.firstName.charAt(0)}${alert.resident.lastName.charAt(0)}`.toUpperCase()
      : '?';

    const childName = alert.resident
      ? `${alert.resident.firstName} ${alert.resident.lastName}`
      : (alert.description ?? '-');

    return (
      <div className={styles.DualHeader}>
        <Link
          to={`/alerts/${parentAlert.uuid}`}
          className={`${styles.DualBlock} ${styles.HeaderBlockParent}`}
          style={parentGradientStyle}
        >
          <Avatar className={styles.ParentAvatar} />
          <div className={styles.AlarmLabel}>
            <i className="icon-alarm"></i>
            <h2>{t(`alert.types.${parentAlert.eventType.toLowerCase()}`)}</h2>
          </div>
          <p className={styles.DualHeaderName}>{parentAlert.description}</p>
          {parentLocation && <p className={styles.DualHeaderSub}>{parentLocation}</p>}
        </Link>

        <div className={styles.Chevron}>&rsaquo;</div>

        <div className={styles.DualBlock} style={alarmGradientStyle}>
          <div className={styles.InitialsAvatar}>{childInitials}</div>
          <div className={styles.AlarmLabel}>
            <i className="icon-alarm"></i>
            <h2>{t(`alert.types.${alert.eventType.toLowerCase()}`)}</h2>
          </div>
          <p className={styles.DualHeaderName}>{childName}</p>
          <p className={styles.DualHeaderSub}>{alert.alarmDescription ?? t('alert.backup.requestsBackup')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Panel className={styles.Panel}>
        {alert.device && (
          <div className={styles.AvatarWrapper}>
            <Avatar
              style={
                showDeviceImage
                  ? {
                      backgroundSize: '80%',
                      backgroundRepeat: 'no-repeat',
                    }
                  : undefined
              }
              src={showDeviceImage ? deviceImage : undefined}
              className={styles.Avatar}
            />
          </div>
        )}
        <div className={styles.AlertInfo} style={alarmGradientStyle}>
          <div
            style={{
              padding: '0 30px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>
              {alert.resident
                ? t('profile.healthLevel') + ': ' + (resident?.healthLevel ?? resident?.katzLevel ?? '-')
                : ''}
            </span>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              {!showDeviceImage && deviceImage && (
                <img style={{ height: 20, width: 20, marginRight: 5 }} src={deviceImage} />
              )}
              {typeof device?.batteryLevel === 'number' && (
                <BatteryIndicator
                  color="white"
                  inaccurate={
                    !device.deviceType || !deviceTypesWithAccurateBatteryPercentage.includes(device.deviceType)
                  }
                  percentage={device.batteryLevel}
                  chargerConnected={device.chargerConnected}
                />
              )}
            </div>
          </div>
          <div className={styles.AlarmLabel}>
            <i className="icon-alarm"></i>
            <h2> {t(`alert.types.${alert.eventType.toLowerCase()}`)}</h2>
          </div>
          <h1>{alert.description}</h1>
          <h2>{livingUnit ? `${livingUnit?.name} - ${resident?.site?.name}` : (resident?.site?.name ?? '')}</h2>
          {alert.locationDescription && <h2>{alert.locationDescription}</h2>}
        </div>
      </Panel>
      {!alert.resident && device?.deviceType && deviceTypesWithOwner.includes(device.deviceType) && (
        <div className="mt-10">
          <AlertBox type="error">{t('alert.detail.noDeviceOwnerWarning', { mac: device?.macAddress ?? '-' })}</AlertBox>
        </div>
      )}
    </>
  );
};

export default AlertHeader;
